import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Button,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  pb,
  getCurrentUser,
  isUserValid,
  hasAdminPermission,
} from "../services/pocketbase";
import ClipUploader from "../components/ClipUploader";
import AdminClipUploader from "../components/AdminClipUploader";
import { Link } from "react-router-dom";

// Function to get game display name
const getGameName = (gameId) => {
  const games = {
    blackops6: "Black Ops 6",
    gta5: "GTA 5",
    other: "Other",
  };
  return games[gameId] || gameId;
};

const Clips = () => {
  const [clips, setClips] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [adminUploaderOpen, setAdminUploaderOpen] = useState(false);
  const [user] = useState(getCurrentUser());
  const isAdmin = hasAdminPermission(user);
  const [votes, setVotes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGame, setSelectedGame] = useState("all");
  const [filteredClips, setFilteredClips] = useState([]);
  const isLoggedIn = isUserValid();
  const [selectedClip, setSelectedClip] = useState(null);

  const fetchClips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isLoggedIn) {
        setLoading(false);
        return;
      }

      const records = await pb.collection("clips").getList(1, 50, {
        sort: "-created",
        expand: "user,clip_votes(clip)",
      });

      // Process votes
      const voteMap = {};
      records.items.forEach((clip) => {
        const clipVotes = clip.expand?.["clip_votes(clip)"] || [];
        voteMap[clip.id] = {
          upvotes: clipVotes.filter((vote) => vote.vote_type === "upvote")
            .length,
          downvotes: clipVotes.filter((vote) => vote.vote_type === "downvote")
            .length,
          userVote:
            clipVotes.find((vote) => vote.user === user?.id)?.vote_type || null,
        };
      });

      setVotes(voteMap);
      setClips(records.items);
    } catch (err) {
      console.error("Error fetching clips:", err);
      setError("Failed to load clips. Please try again later.");
      setClips([]);
    } finally {
      setLoading(false);
    }
  }, [user, isLoggedIn]);

  // Add filtering effect
  useEffect(() => {
    if (clips.length > 0) {
      const filtered = clips.filter((clip) => {
        // Apply game filter
        if (selectedGame !== "all" && clip.game !== selectedGame) {
          return false;
        }
        // Apply search filter
        if (
          searchQuery &&
          !clip.clip_title.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          return false;
        }

        return true;
      });

      setFilteredClips(filtered);
    } else {
      setFilteredClips([]);
    }
  }, [clips, searchQuery, selectedGame]);

  const handleVote = async (clipId, voteType) => {
    if (!user) {
      setError("Please login to vote");
      return;
    }

    try {
      const currentVote = votes[clipId]?.userVote;

      // Remove existing vote if clicking the same button
      if (currentVote === voteType) {
        await pb
          .collection("clip_votes")
          .getFirstListItem(`user="${user.id}" && clip="${clipId}"`)
          .then((record) => {
            return pb.collection("clip_votes").delete(record.id);
          });

        setVotes((prev) => ({
          ...prev,
          [clipId]: {
            upvotes:
              voteType === "upvote"
                ? prev[clipId].upvotes - 1
                : prev[clipId].upvotes,
            downvotes:
              voteType === "downvote"
                ? prev[clipId].downvotes - 1
                : prev[clipId].downvotes,
            userVote: null,
          },
        }));
      } else {
        // Remove existing vote if exists
        if (currentVote) {
          await pb
            .collection("clip_votes")
            .getFirstListItem(`user="${user.id}" && clip="${clipId}"`)
            .then((record) => {
              return pb.collection("clip_votes").delete(record.id);
            });
        }

        // Add new vote
        await pb.collection("clip_votes").create({
          user: user.id,
          clip: clipId,
          vote_type: voteType,
        });

        setVotes((prev) => ({
          ...prev,
          [clipId]: {
            upvotes:
              voteType === "upvote"
                ? prev[clipId].upvotes + 1
                : prev[clipId].upvotes - (currentVote === "upvote" ? 1 : 0),
            downvotes:
              voteType === "downvote"
                ? prev[clipId].downvotes + 1
                : prev[clipId].downvotes - (currentVote === "downvote" ? 1 : 0),
            userVote: voteType,
          },
        }));
      }
    } catch (err) {
      console.error("Error voting:", err);
      setError("Failed to register vote. Please try again.");
    }
  };

  useEffect(() => {
    fetchClips();
  }, [fetchClips]);

  const handleUploaderClose = (refresh) => {
    setUploaderOpen(false);
    if (refresh) {
      fetchClips();
    }
  };

  // Add this function to handle edit button click
  const handleEditClip = (clip) => {
    setSelectedClip(clip);
    setAdminUploaderOpen(true);
  };

  const handleDeleteClip = async (clipId) => {
    if (!isAdmin) {
      setError("You don't have permission to delete clips");
      return;
    }

    if (window.confirm("Are you sure you want to delete this clip?")) {
      try {
        // Get the clip data before deleting it
        const clipToDelete = clips.find((clip) => clip.id === clipId);

        // Delete the clip
        await pb.collection("clips").delete(clipId);

        // Send webhook notification about the deletion
        const webhookData = {
          embeds: [
            {
              title: "Clip Deleted",
              description: `**Clip was deleted by ${user.username}**`,
              color: 0xff0000,
              fields: [
                {
                  name: "Clip Title",
                  value: clipToDelete.clip_title || "Unknown",
                  inline: true,
                },
                {
                  name: "Game",
                  value: getGameName(clipToDelete.game) || "Unknown",
                  inline: true,
                },
                {
                  name: "Clip Link",
                  value: clipToDelete.clip_link || "No link provided",
                },
                {
                  name: "Deleted At",
                  value: new Date().toLocaleString(),
                },
              ],
              timestamp: new Date().toISOString(),
              footer: {
                text: `Admin: ${user.username}`,
              },
            },
          ],
        };

        // Send to webhook endpoint
        const response = await fetch("/api/webhooks/clip-logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(webhookData),
        });

        if (!response.ok) {
          console.error(
            "Failed to send webhook notification for clip deletion"
          );
        }

        // Update the UI
        setClips((prev) => prev.filter((clip) => clip.id !== clipId));
        setError(null);
      } catch (err) {
        console.error("Error deleting clip:", err);
        setError("Failed to delete clip. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading clips...</Typography>
      </Container>
    );
  }

  if (!isLoggedIn) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            borderRadius: 2,
          }}
        >
          <Alert
            severity="info"
            sx={{
              mb: 3,
              width: "80%",
              maxWidth: "500px",
              "& .MuiAlert-message": { fontSize: "1.1rem" },
            }}
          >
            Please login to view clips
          </Alert>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const getVideoId = (youtubeLink) => {
    if (!youtubeLink) return null;

    try {
      // Handle different YouTube URL formats
      let videoId = null;

      // Check if it's already just an ID
      if (/^[a-zA-Z0-9_-]{11}$/.test(youtubeLink)) {
        return youtubeLink;
      }

      // Try to extract from various YouTube URL formats
      const regexPatterns = [
        // youtube.com/watch?v=VIDEO_ID
        // eslint-disable-next-line
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/e\/|youtube\.com\/user\/[^\/]+\/[^\/]+\/|youtube\.com\/[^\/]+\/[^\/]+\/|youtube\.com\/verify_age\?next_url=\/watch%3Fv%3D)([^#\&\?\/]{11})/,
        // youtu.be/VIDEO_ID
        // eslint-disable-next-line
        /youtu\.be\/([^#\&\?\/]{11})/,
        // youtube.com/embed/VIDEO_ID
        // eslint-disable-next-line
        /youtube\.com\/embed\/([^#\&\?\/]{11})/,
      ];

      for (const pattern of regexPatterns) {
        const match = youtubeLink.match(pattern);
        if (match && match[1]) {
          videoId = match[1];
          break;
        }
      }

      // If regex didn't work, try URL parsing as fallback
      if (!videoId) {
        try {
          const url = new URL(youtubeLink);

          // Handle youtube.com/watch?v= format
          if (url.hostname.includes("youtube.com")) {
            videoId = url.searchParams.get("v");
          }
          // Handle youtu.be/ format
          else if (url.hostname === "youtu.be") {
            videoId = url.pathname.substring(1);
          }
        } catch (urlError) {
          console.error("Error parsing URL:", urlError);
        }
      }

      // Validate video ID format (should be 11 characters)
      if (videoId && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
        return videoId;
      }

      return null;
    } catch (err) {
      console.error("Error parsing YouTube URL:", err);
      return null;
    }
  };

  // Inside the return statement, where you render the clip cards
  return (
    <Container sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Gaming Clips</Typography>
        <Box>
          {isUserValid() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUploaderOpen(true)}
              sx={{
                mr: 1,
                backgroundColor: "#c4d4e0",
                "&:hover": {
                  backgroundColor: "#848d94",
                },
              }}
            >
              Submit Clip
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              onClick={() => setAdminUploaderOpen(true)}
            >
              Admin Upload
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            displayEmpty
          >
            <MenuItem value="all">All Games</MenuItem>
            <MenuItem value="blackops6">Black Ops 6</MenuItem>
            <MenuItem value="gta5">GTA 5</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <TextField
          placeholder="Search clips..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
      </Box>

      <Grid container spacing={3}>
        {filteredClips.map((clip) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={clip.id}
            sx={{
              width: {
                xs: "100%", // Full width on mobile
                md: "31.9%", // 30% width on desktop
              },
            }}
          >
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
                {/* eslint-disable-next-line */}
                <iframe
                  src={`https://www.youtube.com/embed/${getVideoId(
                    clip.clip_link
                  )}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {clip.clip_title}
                </Typography>

                {/* Conditional rendering based on game type */}
                {clip.game === "blackops6" ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Black Ops 6 -{" "}
                      {clip.map
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Highest Round: {clip.highest_round}
                    </Typography>
                  </>
                ) : clip.game === "gta5" ? (
                  <Typography variant="body2" color="text.secondary">
                    GTA 5
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Other Game
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary">
                  Submitted by: {clip.discord_username}
                </Typography>

                {/* Vote buttons */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  {/* Vote Buttons */}
                  <Box>
                    <IconButton
                      onClick={() => handleVote(clip.id, "upvote")}
                      color={
                        votes[clip.id]?.userVote === "upvote"
                          ? "primary"
                          : "default"
                      }
                    >
                      <ThumbUpIcon />
                    </IconButton>
                    <Typography component="span" sx={{ mx: 1 }}>
                      {votes[clip.id]?.upvotes || 0}
                    </Typography>
                    <IconButton
                      onClick={() => handleVote(clip.id, "downvote")}
                      color={
                        votes[clip.id]?.userVote === "downvote"
                          ? "error"
                          : "default"
                      }
                    >
                      <ThumbDownIcon />
                    </IconButton>
                    <Typography component="span" sx={{ ml: 1 }}>
                      {votes[clip.id]?.downvotes || 0}
                    </Typography>
                  </Box>

                  {/* Admin Actions */}
                  {isAdmin && (
                    <Box>
                      <IconButton
                        onClick={() => handleEditClip(clip)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteClip(clip.id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredClips.length === 0 && !loading && (
        <Typography align="center" sx={{ mt: 4 }}>
          No clips found. Try adjusting your filters or submit a new clip!
        </Typography>
      )}

      <ClipUploader open={uploaderOpen} onClose={handleUploaderClose} />
      {/* Admin Clip Uploader */}
      {isAdmin && (
        <AdminClipUploader
          open={adminUploaderOpen}
          onClose={(refresh) => {
            setAdminUploaderOpen(false);
            if (refresh) {
              fetchClips();
            }
            setSelectedClip(null); // Reset selected clip when closing
          }}
          editClip={selectedClip}
        />
      )}
    </Container>
  );
};

export default Clips;
